import { Component } from '@theme/component';

/** Feedback and recovery around Horizon's native infinite collection grid. */
class AnnaAdProgress extends Component {
  requiredRefs = ['message', 'retry'];
  #abort;
  #mutations;
  #intersection;
  #near = false;
  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#abort?.abort(); this.#mutations?.disconnect(); this.#intersection?.disconnect(); }
  #setup() {
    this.#abort?.abort(); this.#mutations?.disconnect(); this.#intersection?.disconnect();
    this.#abort = new AbortController();
    const results = this.closest('results-list');
    if (!results) return;
    results.addEventListener('anna:page-error', this.#sync, { signal: this.#abort.signal });
    this.#mutations = new MutationObserver(this.#sync);
    this.#mutations.observe(results, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-anna-load-error', 'data-last-page'] });
    this.#intersection = new IntersectionObserver(entries => { this.#near = entries.some(e => e.isIntersecting); this.#sync(); }, { rootMargin: '350px' });
    this.#intersection.observe(this);
    this.#sync();
  }
  #sync = () => {
    const results = this.closest('results-list');
    const grid = results?.querySelector('[ref="grid"]');
    const last = Number(grid?.dataset.lastPage || 0);
    const page = Number(grid?.querySelector(':scope > [data-page]:last-child')?.dataset.page || 0);
    const failed = results?.dataset.annaLoadError === 'true';
    const loading = this.#near && last > page && !failed;
    // Avoid mutation loops in this component's own status subtree.
    if (this.refs.message.hidden === loading) this.refs.message.hidden = !loading;
    if (this.refs.retry.hidden === failed) this.refs.retry.hidden = !failed;
  };
  async retry() {
    const results = this.closest('results-list');
    this.refs.retry.disabled = true;
    try { await results?.retryNextPage?.(); } finally { if (this.isConnected) { this.refs.retry.disabled = false; this.#sync(); } }
  }
}

/** Stable, deduplicated cross-store grid. It starts only after the main assortment ends. */
class AnnaAdRecommendations extends Component {
  requiredRefs = ['grid', 'more', 'status', 'retry', 'end', 'copy'];
  #abort;
  #mainObserver;
  #intersection;
  #buffer = [];
  #cursor = 1;
  #last = 0;
  #loading = false;
  #active = false;
  #error = false;
  #copy;
  #excluded = new Set();
  #seen = new Set();
  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort(); this.#abort = new AbortController();
    this.#copy = JSON.parse(this.refs.copy.textContent);
    this.#excluded = new Set((this.dataset.excludeIds || '').split(',').filter(Boolean));
    const currentPage = Number(this.refs.grid.dataset.feedPage);
    this.#last = Number(this.refs.grid.dataset.lastPage);
    if (currentPage === 1) {
      this.#buffer = [...this.refs.grid.querySelectorAll(':scope > [data-anna-recommended-id]')];
      this.#cursor = 2;
    }
    this.refs.grid.replaceChildren();
    this.#intersection = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting) && this.#active && !this.#error) this.#fill(); }, { rootMargin: '600px' });
    this.#intersection.observe(this.refs.more);
    const main = document.querySelector('[data-anna-ad-main]');
    if (main) {
      this.#mainObserver = new MutationObserver(this.#checkMain);
      this.#mainObserver.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-last-page'] });
    }
    this.#checkMain();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#abort?.abort(); this.#mainObserver?.disconnect(); this.#intersection?.disconnect(); }
  #checkMain = () => {
    const main = document.querySelector('[data-anna-ad-main]');
    let complete = !main;
    if (main) {
      const grid = main.querySelector('[ref="grid"]');
      if (!grid) complete = !!main.querySelector('.main-collection-grid__empty');
      else {
        const cards = [...grid.querySelectorAll(':scope > [data-product-id]')];
        for (const card of cards) this.#excluded.add(card.dataset.productId);
        const lastPage = Number(grid.dataset.lastPage);
        const maxPage = Math.max(0, ...cards.map(c => Number(c.dataset.page)));
        complete = maxPage >= lastPage && lastPage > 0;
      }
    }
    this.hidden = !complete;
    if (complete && !this.#active) { this.#active = true; this.#fill(); }
  };
  #requestPage = async () => {
    const url = new URL(location.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('page', String(this.#cursor));
    // The store-wide recommendation order stays independent of current collection filters.
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'sort_by') url.searchParams.delete(key);
    url.hash = '';
    const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.any([this.#abort.signal, AbortSignal.timeout(30000)]) });
    if (!response.ok) throw new Error('Recommendation request failed');
    const text = await response.text();
    if (/Liquid error|Liquid syntax error/.test(text)) throw new Error('Recommendation render failed');
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const grid = doc.querySelector('anna-ad-recommendations [ref="grid"]');
    if (!grid || Number(grid.dataset.feedPage) !== this.#cursor) throw new Error('Recommendation page mismatch');
    this.#last = Number(grid.dataset.lastPage);
    this.#buffer.push(...grid.querySelectorAll(':scope > [data-anna-recommended-id]'));
    this.#cursor++;
  };
  #fill = async () => {
    const limit = Number(this.dataset.limit || 48);
    if (this.#loading || !this.#active || this.hidden || this.#seen.size >= limit) return;
    this.#loading = true;
    this.refs.status.textContent = this.#copy.loading;
    this.refs.grid.setAttribute('aria-busy', 'true');
    this.refs.retry.hidden = true;
    const target = Math.min(limit, this.#seen.size + 24);
    try {
      while (this.#seen.size < target && this.isConnected) {
        if (!this.#buffer.length) {
          if (this.#cursor > this.#last) break;
          await this.#requestPage();
          continue;
        }
        const node = this.#buffer.shift();
        const id = node.dataset.annaRecommendedId;
        if (this.#excluded.has(id) || this.#seen.has(id)) continue;
        this.#seen.add(id);
        this.refs.grid.append(node);
      }
      this.#error = false;
      this.refs.status.textContent = '';
      this.dataset.loaded = String(this.#seen.size);
      const done = this.#seen.size >= limit || (!this.#buffer.length && this.#cursor > this.#last);
      this.refs.end.hidden = !done;
      if (done) { this.dataset.complete = 'true'; this.#intersection.unobserve(this.refs.more); }
      else {
        const rect = this.refs.more.getBoundingClientRect();
        if (rect.top < innerHeight + 600 && rect.bottom > -600) queueMicrotask(() => this.#fill());
      }
    } catch (error) {
      if (!this.isConnected || error.name === 'AbortError') return;
      this.#error = true;
      this.refs.status.textContent = this.#copy.error;
      this.refs.retry.hidden = false;
    } finally {
      this.#loading = false;
      if (this.isConnected) this.refs.grid.removeAttribute('aria-busy');
    }
  };
  retry() { this.#error = false; this.#fill(); }
}
if (!customElements.get('anna-ad-progress')) customElements.define('anna-ad-progress', AnnaAdProgress);
if (!customElements.get('anna-ad-recommendations')) customElements.define('anna-ad-recommendations', AnnaAdRecommendations);
